<script setup>
import {mdiMagnify} from "@mdi/js";
import {computed, ref, watch} from "vue";
import {AddressGpsLocation} from "@/graphql/queries/geocoding.graphql";
import {useLazyQuery} from "@vue/apollo-composable";
import {useLastResult} from "@/graphql/composable";
import * as leaflet from "leaflet";

const emit = defineEmits(['select']);

const searchQuery = ref('');
const queryNotEmpty = computed(() => {
    return searchQuery.value && searchQuery.value.length > 0;
})
const {result, load, refetch, loading} = useLazyQuery(AddressGpsLocation);
const searchResults = useLastResult(result, []);

const selectedIndex = ref(null);

watch(searchResults, (value) => {
    if (value !== null && value.length === 1) {
        selectLocation(value[0]);
        selectedIndex.value = 0;
    }
})

function doSearch() {
    if (selectedIndex.value === null && searchQuery.value.length > 0) {
        load(null, {query: searchQuery.value}) || refetch({query: searchQuery.value});
        selectedIndex.value = null;
    } else if (searchResults.value.length > 0) {
        selectLocation(searchResults.value[selectedIndex.value]);
    }
}

function selectLocation(location) {
    emit('select', {latlng: leaflet.latLng(location.latitude, location.longitude)})
}

function selectDown() {
    if (searchResults.value === null || searchResults.value.length === 0) {
        return;
    }

    if (selectedIndex.value === null) {
        selectedIndex.value = 0;
    } else {
        selectedIndex.value = (selectedIndex.value + 1) % searchResults.value.length;
    }
}

function selectUp() {
    if (searchResults.value === null || searchResults.value.length === 0) {
        return;
    }

    if (selectedIndex.value === null) {
        selectedIndex.value = searchResults.value.length - 1;
    } else {
        selectedIndex.value = (selectedIndex.value - 1 + searchResults.value.length) % searchResults.value.length;
    }
}

function isSelected(index) {
    return selectedIndex.value === index;
}
</script>

<template>
    <div class="hpo-map-search d-none d-lg-block">
        <v-toolbar class="px-4" rounded="lg" border>
            <v-text-field v-model="searchQuery"
                          density="compact"
                          variant="solo"
                          flat
                          hide-details
                          single-line
                          clearable
                          @change="selectedIndex = null"
                          @keyup.enter="doSearch"
                          @keyup.down="selectDown"
                          @keyup.up="selectUp" />
            <template v-slot:append>
                <v-btn class="ml-2" @click="doSearch" :icon="mdiMagnify" density="comfortable" />
            </template>
        </v-toolbar>
        <div v-if="loading" class="hpo-map-search__result-list bg-white rounded-lg text-center">
            <v-progress-circular indeterminate/>
        </div>
        <div v-else-if="queryNotEmpty && searchResults !== null && searchResults.length > 0"
             class="hpo-map-search__result-list bg-white rounded-lg">
            <div v-for="(result, index) in searchResults"
                 class="hpo-map-search__result text-truncate text-subtitle-1"
                 :class="{'hpo-map-search__result--active': isSelected(index)}"
                 @click="selectLocation(result)">
                {{ result.address }}
            </div>
        </div>
        <div v-else-if="queryNotEmpty && searchResults === null" class="hpo-map-search__result-list bg-white rounded-lg">
            <v-empty-state :title="pgettext('message', 'Address not found.')" />
        </div>
    </div>
</template>
